/**************************************************************************************************
 *
 *  This is the main container for all project related views: Project Detail (hierarch), Project
 *  Edit/Creation, Build Edit/Creation, Task Detail, Field Task Detail
 *
 *  This views are individual components that will fit into the detail-container parent element
 *  defined in this container.
 *
**************************************************************************************************/

import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Home from '../../containers/Home';
import ErrorPage from '../../containers/ErrorPage';
import BasePage from '../../baseClasses/BasePage';
import Header from '../../components/Header';
import animator from '../../helpers/animator';
import ProjectsNav from '../../components/ProjectsNav';
import TaskDisplay from './TaskDisplay';
import TasksContainerTasks from './TasksContainerTasks';
import './TasksContainer.scss';
import dataModel from '../../models/DataModel';
import CollectionDataSource from '../../dao/DataSource/CollectionDataSource';
import { task_complete } from '../../dao/TasksDAO';
import AnalyticsLogger from '../../helpers/analytics/analyticsLogger';

const projectDocumentsDescriptor = {
  project: {
    additionalFields: ['has_alerts', 'has_unread_alerts', 'has_delays'],
    unique_documents: {},
    reference_documents: {},
    location: {},
    tasks: {},
  },
};

const projectDescriptor = {
  project: {
    additionalFields: [
      'has_alerts',
      'has_unread_alerts',
      'has_delays',
      'tasks_count',
      'tasks_completed_count',
      'completion_steps_count',
      'completion_steps_completed_count',
    ],
    location: {},
  },
};

class TasksContainer extends BasePage {

  constructor(props) {
    super(props);

    // Initialize all expected state variable here

    this.state = {
      viewName: 'ProjectTasks',
      allProjects: true,
      resetScroll: false,
    };

    this.animator = new animator();
    const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });
  }

  handleFavoritesRetrieval(favoriteProjects) {
    this.setState({ favoriteProjects });
    if (this.projectsDataSource) {
      const perPage = Math.floor((window.innerHeight - 220) / 97) - Math.min(favoriteProjects && favoriteProjects.projects ? favoriteProjects.projects.length : 0, 2);
      this.projectsDataSource.perPage = perPage;
      this.projectsDataSource.page = 1;
      this.projectsDataSource.current();
    }
  }

  handleProjectsRetrieval(navigationProjects, action, acData, fetchedData) {

    this.setState({ navigationProjects, isInitialized: true });
    if (acData && acData.sub_type == 'change_favorite' && acData.updating_user_id == this.props.currentUser.id) {
      if (this.favoritesDataSource) {
        this.favoritesDataSource.current();
      }
    }
  }

  handleCompanyRetrieval(companies) {
    this.setState({ companies });
  }

  handleFinishMount() {
    const { currentCompany, currentUser } = this.props;
    
    if (currentCompany && currentUser) {
      const perPage = Math.floor((window.innerHeight - 220) / 97) - 2;
      if (this.props.currentCompany && this.props.currentUser) {
        this.projectsDataSource = new CollectionDataSource('ProjectsCollection', 'Project', projectDescriptor, this.handleProjectsRetrieval, 'tasks_project_nav', perPage, null,
          this.props.currentUser.id, this.props.currentCompany.id, 'tasks');
        this.favoritesDataSource = new CollectionDataSource('FavoritesCollection', 'Project', projectDescriptor, this.handleFavoritesRetrieval,
          this.props.currentUser.id, this.props.currentCompany.id, true);
        this.companiesDataSource = new CollectionDataSource('CompaniesCollection', 'Company', { companies: {} }, this.handleCompanyRetrieval, null, null,
          this.props.currentUser.id, this.props.currentCompany.id);
        
        this.favoritesDataSource.current();
        this.companiesDataSource.current();
        
      }
    }
  }

  componentDidMount() {
    super.componentDidMount();
    if (this.props.match.params.project_id && this.props.passthroughUri) {
      dataModel.setPassthroughUri(null);
      this.handleFinishMount();
    }
    else if (this.props.match.params.task_id && this.props.passthroughUri) {
      dataModel.setPassthroughUri(null);
      this.handleFinishMount();
    }
    else {
      this.handleFinishMount();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { currentCompany, currentUser, match } = this.props;
    const { navigationProjects, favoriteProjects } = this.state;
    const userHasProjects = (
      (navigationProjects && navigationProjects.ids && navigationProjects.ids.length) || 
      (favoriteProjects && favoriteProjects.ids && favoriteProjects.ids.length)
    );
    
    if ((!prevProps.match && match) || prevProps.match.params != match.params) {
      if (!match.params || !match.params.project_id) {
        this.navigateToProject('all');
      }
      else if (userHasProjects) {
        this.handlePageNavigation(match.params);
      }
    }
    else if (navigationProjects != prevState.navigationProjects) {
      if (!match.params || !match.params.project_id) {
        this.navigateToProject('all');
      }
      else if (userHasProjects) {
        this.handlePageNavigation(match.params);
      }
    }
    else if (currentUser && currentCompany && (currentCompany != prevProps.currentCompany || currentUser != prevProps.currentUser)) {
      if (!this.projectsDataSource) {
        this.handleFinishMount();
      }
      this.handleSelectProject('all');
    }

    if (prevProps.location.state != this.props.location.state && this.props.location.state && this.props.location.state.showArchived) {
      this.setState({ showArchived: true });
    }
  }

  componentWillUnmount() {
    if (this.favoritesDataSource) {
      this.favoritesDataSource.release();
      this.favoritesDataSource = null;
    }
    if (this.projectsDataSource) {
      this.projectsDataSource.release();
      this.projectsDataSource = null;
    }
    if (this.companiesDataSource) {
      this.companiesDataSource.release();
      this.companiesDataSource = null;
    }
  }
  
  handleBrowserNavigation(params, project) {
    this.useParams = false;
    if (params.task_id) {
      if (!project) {
        if (params.project_id) {
          dataModel.projectsDAO.getProject(parseInt(params.project_id, 10), { recordDescriptor: projectDocumentsDescriptor }).then(project => {
            if(project && project.company_id == this.props.currentCompany.id){
              dataModel.tasksDAO.getTask(parseInt(params.task_id, 10), { recordDescriptor: task_complete }).then((task) => {
                this.handleSetTaskDisplay(project, task);
              });
            }
          });
        }
      }
      if((project && this.props.currentCompany && project.company_id == this.props.currentCompany.id) || params.project_id == 'all'){
        dataModel.tasksDAO.getTask(parseInt(params.task_id, 10), { recordDescriptor: task_complete }).then((task) => {
          this.handleSetTaskDisplay(project, task);
        });
      }
    }
    else if (params.project_id) {
      if (params.project_id == 'all') {
        this.handleSelectProject('all');
      }
      else {
        dataModel.projectsDAO.getProject(parseInt(params.project_id, 10), { recordDescriptor: projectDocumentsDescriptor }).then(project => {
          this.handleSelectProject(project);
        });
      }
    }
    else{
      if(Object.keys(this.props.match.params).length == 0 && this.props.match.params.constructor == Object){
        this.setView('ProjectTasks', 'all', null, null, null);
      }
      else{
        this.props.dataModel.displayInfoErrorMessage('Company restriction error', 4000, 2);
        this.setView('restricted', null, null, null);      
      }
    }
  }

  handlePageNavigation(params) {
    if (params && params.project_id) {
      if (params.task_id) {
        this.handleBrowserNavigation(params, this.state.selectedProject);
      }
      else if (params.project_id == 'all') {
        this.setState({ allProjects: true });
        this.handleBrowserNavigation(params, null);
      }
      else {
        dataModel.projectsDAO.getProject(parseInt(params.project_id, 10), { recordDescriptor: projectDocumentsDescriptor }).then((project) => {
          this.setState({ selectedProject: project, allProjects: false });
          this.handleBrowserNavigation(params, project);
        });
      }
    }
  }

  navigateToProject(project) {
    this.ignoreQuery = false;
    if (project == 'all') {
      this.props.history.push('/tasks/project/all');
    }
    else {
      this.props.history.push('/tasks/project/' + (project ? project.id : ''));
    }
  }

  setView(viewName, project, build, task, breadCrumbList, refreshView) {
    this.setState({
      viewName: viewName, selectedProject: (project == 'all' ? null : project), allProjects: project == 'all', selectedBuild: build ? build : this.state.selectedBuild, selectedTask: task,
      breadCrumbList: breadCrumbList,
    });
    if (refreshView){
      this.setState({ refreshView: !this.state.refreshView });
    }
  }

  handleSelectProject(project) {
    this.saveAllProjects = null;
    if (project == 'all') {

      // console.log("HANDLE SELECT PROJECT")

      this.setView('ProjectTasks', 'all', null, null, null);
    }
    else if (project) {
      if (!project.tasks || project.tasks.length == 0) {
        dataModel.projectsDAO.getProject(project.id, { recordDescriptor: projectDocumentsDescriptor }).then(fetchedProject => {
          this.setView('ProjectTasks', fetchedProject, -1, null, null);
        });
      }
      else {
        this.setView('ProjectTasks', project, -1, null, null);
      }
    }
  }

  handleSetTaskDisplay(project, task) {
    this.saveAllProjects = this.state.allProjects;
    this.setView('TaskDisplay', project, null, task);
  }

  getSystemPopupView() {
    if (this.props.systemPopup) {
      return this.props.systemPopup;
    }
    else {
      return null;
    }
  }

  handleMapExpansion(expanded){
    this.setState({ mapExpanded: expanded });
  }
  
  handleSelectTaskView(view){
    this.setState({ selectedTaskView: view });
  }

  handleFilterProjects(filter) {
    this.projectsDataSource.perPage = Math.floor((window.innerHeight - 220) / 97) - Math.min(this.state.favoriteProjects && this.state.favoriteProjects.projects ? this.state.favoriteProjects.projects.length : 0, 2);
    if (filter) {
      const { project_type, state, showArchived, statuses, markets } = filter;
      this.projectsDataSource.filter = {
        projectTypeId: project_type ? project_type.id : null,
        state,
        showArchived,
        statuses,
        markets,
      };
      this.favoritesDataSource.filter = {
        projectTypeId: project_type ? project_type.id : null,
        state,
        showArchived,
        statuses,
        markets,
      };
    }
    else {
      this.projectsDataSource.filter = {
        projectTypeId: null,
        state: null,
        showArchived: null,
        statuses: null,
        markets: null,
      };
      this.favoritesDataSource.filter = {
        projectTypeId: null,
        state: null,
        showArchived: null,
        statuses: null,
        markets: null,
      };
    }
    return this.favoritesDataSource.current();
  }
  
  handleProjectSearch(filter, searchString, perPage) {
    this.projectsDataSource.perPage = Math.floor((window.innerHeight - 220) / 97) - Math.min(this.state.favoriteProjects && this.state.favoriteProjects.ids ? this.state.favoriteProjects.ids.length : 0, 2);
    if (!searchString) {
      this.projectsDataSource.searchString = null;
    }
    else {
      this.projectsDataSource.searchString = searchString;
    }
    return this.projectsDataSource.current();
  }

  handleGetNextPage(perPage) {
    return this.projectsDataSource.next();
  }

  handleRemoveFavorite(project_id) {
    dataModel.projectsDAO.setFavoriteProject(project_id, false, null, { recordDescriptor: projectDescriptor });
    this.analyticsLogger.logProjectListEvent('ProjectListUnfavorite');
  }

  handleMakeFavorite(project_id) {
    dataModel.projectsDAO.setFavoriteProject(project_id, true, null, { recordDescriptor: projectDescriptor });
    this.analyticsLogger.logProjectListEvent('ProjectListFavorite');
  }

  render() {
    if (this.props.catastrophicError) {
      return <ErrorPage catastrophicError={this.props.catastrophicError} />;
    }
    let waitSpinner = null;
    if (this.props.inProgress) {
      waitSpinner = <Home type="transparent" />;
    }
    if (!this.props.dataModel.textMap) {
      this.props.dataModel.textMap = this.textMap;
    }
    if (!this.props.currentUser || !this.props.currentCompany || !this.props.permissions) {
      return <Home type={'projects'} />;
    }
    this.setWhiteLabels(this.props.currentCompany);

    const { viewName, selectedProject, selectedBuild, selectedTask, breadCrumbList } = this.state;

    // any new ones?

    let view = null;

    switch (viewName) {
    case 'ProjectTasks':
      view = <TasksContainerTasks

          key={'project-tasks-' + (selectedProject ? selectedProject.id : this.props.currentCompany.id)}
          id={'project-tasks' + (selectedProject ? selectedProject.id : this.props.currentCompany.id)}
          userProject={selectedProject}
          allProjects={this.state.allProjects}
          build={selectedBuild}
          selectTask={this.handleSetTaskDisplay}
          breadCrumbList={breadCrumbList}
          inProjectsTab={false}
          handleMapExpansion={this.handleMapExpansion}
          mapExpanded={this.state.mapExpanded}
          selectedTaskView={this.state.selectedTaskView}
          handleSelectTaskView={this.handleSelectTaskView}
          projects={this.state.navigationProjects}
          showArchived={this.state.showArchived}
          refreshView={this.state.refreshView}
        />;
      break;
    case 'TaskDisplay':
      let tBreadCrumbList = breadCrumbList;
      if (!breadCrumbList) {
        tBreadCrumbList = [];
        tBreadCrumbList.push(selectedProject);
      }
      view = <TaskDisplay
          key={'tasks-display-' + selectedTask.id}
          id={'task-crud-' + selectedTask.id}
          userProject={selectedProject}
          taskId={selectedTask.id}
          handleTaskSaved={this.handleTaskSaved}
          editing={false} build={selectedBuild}
          selectProject={this.handleSelectProject}
          breadCrumbList={tBreadCrumbList}
          allProjects={this.state.allProjects || this.saveAllProjects}
          handleMapExpansion={this.handleMapExpansion}
          refreshView={this.state.refreshView}
          mapExpanded={this.state.mapExpanded} />;
      break;
    case 'restricted':
      view = <div className='restricted-message'>The selected task is not part of the current company.<br /> Please select the correct company to view.</div>;
      break;
    default:
      break;
    }

    const path = this.props.history.parseParameters();
    
    const projectNavigator = 
      <ProjectsNav 
        selectedProjectId={path.params.project_id}
        allProjects={this.state.allProjects}
        selectedCompany={(this.props.selectedCompany ? this.props.selectedCompany : this.props.currentCompany)} 
        handleProjectSearch={this.handleProjectSearch}
        handleFilterProjects={this.handleFilterProjects} 
        handleMakeFavorite={this.handleMakeFavorite} 
        handleRemoveFavorite={this.handleRemoveFavorite}
        setProjectHierarchy={this.handleSelectProject} 
        navigationProjects={this.state.navigationProjects} 
        favorites={this.state.favoriteProjects} 
        getNextPage={this.handleGetNextPage}
        addButtonEnabled={false}
        inProjectsTab={false}
        refreshView={this.state.refreshView}
        resetScroll={this.state.resetScroll}
        update={this.state.updateToggle}
        isMarketFilterEnabled={true}
      />;
    const systemPopup = this.getSystemPopupView();

    let header = null;
    if (!this.props.useProjectTemplates) {
      header = <Header companies={this.state.companies} selectedPage="tasks" />;

    }

    return (

      <div className="tasks-container"
        ref={(element) => { this.mainContainer = element; }}>
        {!this.props.useProjectTemplates && systemPopup}
        {header}
        {waitSpinner}
        <div className="tasks-body">
          <div className={'nav-container'}>
            {projectNavigator}
          </div>
          <div className="detail-container" id="tasks-detail-container" >
            {view}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    catastrophicError: state.main.catastrophicError,
    inProgress: state.main.inProgress,
    dataModel: dataModel,
    permissions: state.new_users ? state.new_users.permissions : null,
    systemPopup: state.main.systemPopup,
    currentUser: state.new_users.currentUser,
    passthroughUri: state.main.passthroughUri,
    currentCompany: state.new_companies.currentCompany,
    needsLogin: state.main ? state.main.needsLogin : null,
  };
};

export default withRouter(connect(mapStateToProps)(TasksContainer));



// WEBPACK FOOTER //
// ./src/containers/TasksContainer/index.js